import React, { useRef, useEffect, useState, memo } from "react";
import * as d3 from "d3";
import ChartLoader from "../../../Shared/Loader";

const BarChart = ({
  data,
  height = 300,
  xLabel = "Range",
  yLabel = "Frequency",
  isLoading,
  noDataText="Data not available!"
}) => {
  const wrapperRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);

  // Auto width
  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new ResizeObserver(entries => {
      setWidth(entries[0].contentRect.width);
    });

    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!data || !data.length || !width) return;

    const margin = { top: 20, right: 20, bottom: 70, left: 60 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // 🔹 X Scale (CATEGORIES)
    const x = d3
      .scaleBand()
      .domain(data.map(d => d.x))
      .range([0, innerWidth])
      .padding(0.2);

    // 🔹 Y Scale
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, d => d.y) || 1])
      .nice()
      .range([innerHeight, 0]);

    // 🔹 Bars
    g.selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", d => x(d.x))
      .attr("y", d => y(d.y))
      .attr("width", x.bandwidth())
      .attr("height", d => innerHeight - y(d.y))
      .attr("fill", "#4e79a7");

    // 🔹 X Axis
    g.append("g")
      .attr("transform", `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .attr("transform", "rotate(-40)")
      .attr("font-size", "8px")
      .style("text-anchor", "end");

    // 🔹 Y Axis
    g.append("g").call(d3.axisLeft(y));

    // 🔹 X Label
    g.append("text")
      .attr("x", innerWidth / 2)
      .attr("y", innerHeight + 55)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("fill", "#5c5b5b")
      .text(xLabel);

    // 🔹 Y Label
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -innerHeight / 2)
      .attr("y", -45)
      .attr("text-anchor", "middle")
      .attr("font-size", "14px")
      .attr("fill", "#5c5b5b")
      .text(yLabel);

  }, [data, width, height, xLabel, yLabel]);

  return (
    <div ref={wrapperRef} style={{ width: "100%" }}>
      {
        isLoading ?
          <div style={{ height: height }}>
            <ChartLoader zIndex={1} />
          </div>
          :
          data?.length ?
            <svg ref={svgRef} height={height} />
            :
            <div style={{ height: height }} className="d-flex justify-content-center align-items-center">
              <h4>{noDataText}</h4>
            </div>
      }

    </div>
  );
};

export default memo(BarChart);
